import type { KeycloakTokenParsed } from 'keycloak-js';
import type Keycloak from 'keycloak-js';
import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';

import type { LogoutDTO } from '@nora-monorepo/nora-api-client';

import { useApiStore } from '#ui-shared/stores/api/api-store';
import { UserLoggingContext } from '#ui-shared/utils/logger/contexts/log-contexts';
import { appLogger } from '#ui-shared/utils/logger/LoggerClass';

// All in seconds
const SAFETY_MARGIN = 60 * 4; // Start refresh 4 minutes before
const RETRY_DELAY = 5;
const MAX_RETRIES = 5;

let retries = 0;

let refreshTimeout: ReturnType<typeof setTimeout>;

/**
 * Opinion wanted: split this up in authStore and userStore? Or is it okay like this for now
 */
export const useAuthStore = defineStore('authStore', () => {
  const keycloak = shallowRef<Keycloak>();
  const token = ref<string>();
  const tokenParsed = ref<KeycloakTokenParsed>();

  const api = useApiStore();

  function updateToken(newToken: string): void {
    token.value = newToken;

    if (keycloak.value) {
      keycloak.value.token = newToken;
    }
  }

  /**
   * Logs out the current user on the given deviceId, and redirects to Keycloak login page.
   */
  async function logout(): Promise<void> {
    const deviceId = localStorage.getItem('deviceId') || undefined;

    const tokens: LogoutDTO = {
      clientId: keycloak.value?.clientId,
      deviceId,
    };

    if (keycloak.value) {
      tokens.accessToken = keycloak.value.token;
      tokens.refreshToken = keycloak.value.refreshToken;
      tokens.clientId = keycloak.value.clientId;
    }

    const loginUrl = await keycloak.value?.createLoginUrl();

    await api.nexusUser.logout({
      logoutDTO: tokens,
    });

    clearLocalStorageExcept();

    if (loginUrl) {
      window.location.href = loginUrl;
    }
  }

  function getRefreshTime(
    token: KeycloakTokenParsed,
    defaultValue = 6000,
  ): number {
    if (token.exp) {
      const now = Math.floor(Date.now() / 1000);

      return Math.max((token.exp - now) * 1000, defaultValue);
    }

    return defaultValue;
  }

  async function checkAccessToken(): Promise<void> {
    console.debug('Checking access token...');

    if (!keycloak.value) {
      throw new Error('No keycloak instance found, can not refresh token.');
    }

    try {
      const refreshed = await keycloak.value.updateToken(SAFETY_MARGIN);

      if (refreshed && keycloak.value.token && keycloak.value.tokenParsed) {
        console.debug('Token refreshed');
        const authStore = useAuthStore();

        authStore.updateToken(keycloak.value.token);
      }

      setupTokenRefreshTimer(keycloak.value);
    }
    catch (error) {
      appLogger.error({
        message: `Failed to refresh token.`,
        context: {
          user: new UserLoggingContext({ userId: keycloak.value.tokenParsed?.sub || 'unknown' }),
        },
        sensitiveContext: {
          error,
          token: keycloak.value.token,
        },
      });

      if (retries < MAX_RETRIES) {
        refreshTimeout = setTimeout(checkAccessToken, RETRY_DELAY * 1000);

        retries++;
      }
      else {
        appLogger.error({
          message: `Max retries reached for refreshing token, logging out user.`,
          context: {
            user: new UserLoggingContext({ userId: keycloak.value.tokenParsed?.sub || 'unknown' }),
          },
        });

        keycloak.value.logout();
      }
    }
  }

  function setupTokenRefreshTimer(keycloak: Keycloak): void {
    if (!keycloak.tokenParsed) {
      throw new Error('No keycloak token given, can not setup refresh timer.');
    }

    if (refreshTimeout) {
      clearTimeout(refreshTimeout);
    }

    const refreshTime
      = getRefreshTime(keycloak.tokenParsed) - SAFETY_MARGIN * 1000;

    refreshTimeout = setTimeout(checkAccessToken, refreshTime);
  }

  /**
   * Removes all items from localStorage except the ones in the whitelist. Should be called on logout to prevent stale data from previous sessions.
   * If whitelist is empty, all keys will be cleared.
   */
  function clearLocalStorageExcept(whitelist: string[] = []): void {
    const keysInLocalStorage = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        keysInLocalStorage.push(key);
      }
    }

    const keysToRemove = keysInLocalStorage.filter(key => !whitelist.includes(key));

    keysToRemove.forEach(key => localStorage.removeItem(key));
  }

  return {
    keycloak,
    token,
    tokenParsed,
    updateToken,
    logout,
    setupTokenRefreshTimer,
    checkAccessToken,
  };
});
